import { useNavigate } from 'react-router-dom';
import { useWorkspace } from '@/stores/workspace.store';
import { useUi } from '@/stores/ui.store';
import { PlatformIcon, Badge, WidgetCard } from '@/components/wireframe/ui';
import { connections, recentSources, samplePosts, contentItems } from '@/lib/wireframe-data';

/**
 * Workspace overview — quick actions, KPIs, performance charts, and recent-activity
 * widgets. Ported to wireframe fidelity from `dev-spec/v1/contenthub-app.jsx`.
 * Uses demo data until the analytics/drafts GraphQL queries are wired (DEV-3 backlog).
 */
export default function DashboardRoute() {
  const nav = useNavigate();
  const { toast } = useUi();
  const liveCredits = useWorkspace((s) => s.credits);
  const credits = liveCredits || 450;

  const drafts = samplePosts.filter((p) => ['pending', 'approved', 'draft', 'review'].includes(p.status));

  return (
    <div className="p-8 space-y-6">
      {/* Quick actions */}
      <div>
        <h2 className="text-sm font-semibold text-stone-500 uppercase tracking-wide mb-3">Quick actions</h2>
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
          {[
            { icon: '✦', label: 'Create new post', desc: 'Manual or AI-generated', color: 'bg-violet-50 text-violet-600', go: () => nav('/app/create') },
            { icon: '📄', label: 'Add content', desc: 'Add a link or idea to your library', color: 'bg-blue-50 text-blue-600', go: () => nav('/app/content?add=1') },
            { icon: '⟲', label: 'Add source', desc: 'RSS or web scrape', color: 'bg-emerald-50 text-emerald-600', go: () => nav('/app/sources') },
            { icon: '⬡', label: 'Connect platform', desc: 'Link social accounts', color: 'bg-amber-50 text-amber-600', go: () => nav('/app/connections') },
          ].map((item) => (
            <button key={item.label} onClick={item.go} className="group flex items-center gap-4 p-4 bg-white border border-stone-200 rounded-xl hover:border-stone-300 hover:shadow-sm transition-all text-left w-full">
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center text-xl ${item.color}`}>{item.icon}</div>
              <div><p className="font-semibold text-stone-900">{item.label}</p><p className="text-sm text-stone-500">{item.desc}</p></div>
            </button>
          ))}
        </div>
      </div>

      {/* Analytics overview */}
      <div>
        <h2 className="text-sm font-semibold text-stone-500 uppercase tracking-wide mb-3">Analytics overview</h2>
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
          {[
            { label: 'Total posts', value: '156', trend: '+12%', trendLabel: 'vs last month', icon: '📊', iconBg: 'bg-violet-50 text-violet-600' },
            { label: 'Content items', value: '47', trend: '+5', trendLabel: 'this week', icon: '◰', iconBg: 'bg-blue-50 text-blue-600' },
            { label: 'Scheduled', value: '8', sub: 'Next: Today at 2:00 PM', icon: '▦', iconBg: 'bg-amber-50 text-amber-600' },
            { label: 'Drafts pending', value: '12', warn: '4 need review', icon: '◳', iconBg: 'bg-rose-50 text-rose-600' },
          ].map((stat) => (
            <div key={stat.label} className="bg-white border border-stone-200 rounded-xl p-5">
              <div className="flex items-start justify-between mb-3">
                <div><p className="text-sm text-stone-500 mb-1">{stat.label}</p><p className="text-3xl font-bold text-stone-900">{stat.value}</p></div>
                <div className={`w-10 h-10 rounded-lg flex items-center justify-center text-lg ${stat.iconBg}`}>{stat.icon}</div>
              </div>
              {stat.trend && <div className="flex items-center gap-2"><span className="text-xs font-medium text-emerald-600 bg-emerald-50 px-2 py-0.5 rounded-full">{stat.trend}</span><span className="text-xs text-stone-400">{stat.trendLabel}</span></div>}
              {stat.sub && <p className="text-xs text-stone-500">{stat.sub}</p>}
              {stat.warn && <p className="text-xs text-amber-600 font-medium">{stat.warn}</p>}
            </div>
          ))}
        </div>
      </div>

      {/* Performance charts */}
      <div className="grid gap-4 grid-cols-1 lg:grid-cols-3">
        <div className="bg-white border border-stone-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4"><h3 className="font-semibold text-stone-900">Posts by platform</h3><span className="text-xs text-stone-400">Last 7 days</span></div>
          <div className="space-y-3">
            {[{ platform: 'linkedin', name: 'LinkedIn', count: 23, percent: 40 }, { platform: 'twitter', name: 'X / Twitter', count: 18, percent: 31 }, { platform: 'facebook', name: 'Facebook', count: 12, percent: 21 }, { platform: 'instagram', name: 'Instagram', count: 5, percent: 8 }].map((item) => (
              <div key={item.platform} className="flex items-center gap-3">
                <PlatformIcon platform={item.platform} />
                <div className="flex-1">
                  <div className="flex items-center justify-between mb-1"><span className="text-sm text-stone-700">{item.name}</span><span className="text-sm font-medium text-stone-900">{item.count}</span></div>
                  <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden"><div className="h-full bg-violet-500 rounded-full" style={{ width: `${item.percent}%` }} /></div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-white border border-stone-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4"><h3 className="font-semibold text-stone-900">Publishing activity</h3><span className="text-xs text-stone-400">Last 7 days</span></div>
          <div className="flex items-end justify-between h-32">
            {['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'].map((day, i) => {
              const heights = [45, 70, 55, 90, 60, 20, 30];
              return <div key={day} className="flex flex-col items-center gap-2"><div className="w-8 bg-violet-100 rounded-t hover:bg-violet-200 transition-colors" style={{ height: `${heights[i]}%` }} /><span className="text-xs text-stone-400">{day}</span></div>;
            })}
          </div>
        </div>
        <div className="bg-white border border-stone-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-4"><h3 className="font-semibold text-stone-900">Credits overview</h3></div>
          <div className="text-center mb-4"><p className="text-4xl font-bold text-stone-900">{credits}</p><p className="text-sm text-stone-500">credits remaining</p></div>
          <div className="h-2 bg-stone-100 rounded-full overflow-hidden mb-4"><div className="h-full bg-gradient-to-r from-violet-500 to-purple-500 rounded-full" style={{ width: `${Math.min(100, (credits / 1000) * 100)}%` }} /></div>
          <div className="space-y-1 text-xs">
            {[['AI Validator', '5⚡ per check'], ['AI Generation', '10⚡ per post'], ['AI Rewrite', '2⚡ per rewrite']].map(([k, v]) => (
              <div key={k} className="flex justify-between"><span className="text-stone-500">{k}</span><span className="text-stone-700">{v}</span></div>
            ))}
          </div>
          <button onClick={() => nav('/app/billing')} className="w-full mt-4 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 text-sm font-medium">Buy more credits</button>
        </div>
      </div>

      {/* Widgets */}
      <div className="grid gap-4 grid-cols-1 lg:grid-cols-3">
        <WidgetCard title="Recent sources" action={() => nav('/app/sources')} actionLabel="View all">
          <div className="space-y-2">
            {recentSources.map((source) => (
              <div key={source.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-stone-50 transition-colors">
                <div className="flex items-center gap-3">
                  <div className={`w-8 h-8 rounded flex items-center justify-center text-xs font-bold ${source.type === 'RSS' ? 'bg-orange-100 text-orange-600' : 'bg-blue-100 text-blue-600'}`}>{source.type === 'RSS' ? 'R' : 'W'}</div>
                  <div><p className="text-sm font-medium text-stone-900">{source.name}</p><p className="text-xs text-stone-400">{source.lastFetch}</p></div>
                </div>
                <div className="flex items-center gap-2">
                  {source.newItems > 0 && <span className="text-xs font-medium text-violet-600 bg-violet-50 px-2 py-0.5 rounded-full">+{source.newItems}</span>}
                  <button onClick={() => nav('/app/create')} className="text-xs text-violet-600 hover:text-violet-700 font-medium">Create post</button>
                </div>
              </div>
            ))}
          </div>
          <button onClick={() => nav('/app/sources')} className="w-full mt-3 py-2 border border-dashed border-stone-300 rounded-lg text-sm text-stone-500 hover:text-stone-700 hover:border-stone-400 transition-colors">+ Add new source</button>
        </WidgetCard>

        <WidgetCard title="Pending drafts" action={() => nav('/app/drafts')} actionLabel="View all">
          <div className="space-y-2">
            {drafts.slice(0, 3).map((draft) => (
              <div key={draft.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-stone-50 transition-colors">
                <PlatformIcon platform={draft.platforms[0]} />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-stone-900 truncate">{draft.title}</p>
                  <p className="text-xs text-stone-400 truncate">{draft.caption}</p>
                </div>
                <Badge status={draft.status} size="xs" />
              </div>
            ))}
          </div>
          <div className="flex gap-2 mt-3">
            <button onClick={() => nav('/app/drafts')} className="flex-1 py-2 text-sm text-stone-600 hover:bg-stone-50 border border-stone-200 rounded-lg">Review all</button>
            <button onClick={() => nav('/app/create')} className="flex-1 py-2 text-sm text-white bg-violet-600 hover:bg-violet-700 rounded-lg font-medium">New draft</button>
          </div>
        </WidgetCard>

        <WidgetCard title="Recent content" action={() => nav('/app/content')} actionLabel="View all">
          <div className="space-y-2">
            {contentItems.slice(0, 3).map((item) => (
              <div key={item.id} className="flex items-center justify-between p-2 rounded-lg hover:bg-stone-50 transition-colors">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-stone-900 truncate">{item.title}</p>
                  <p className="text-xs text-stone-400">{item.source} · {item.added}</p>
                </div>
                <button onClick={() => nav('/app/create')} className="text-xs text-violet-600 hover:text-violet-700 font-medium ml-2 whitespace-nowrap">Create post</button>
              </div>
            ))}
          </div>
          <button onClick={() => nav('/app/content')} className="w-full mt-3 py-2 border border-dashed border-stone-300 rounded-lg text-sm text-stone-500 hover:text-stone-700 hover:border-stone-400 transition-colors">+ Add content</button>
        </WidgetCard>
      </div>

      {/* Connected platforms */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-sm font-semibold text-stone-500 uppercase tracking-wide">Connected platforms</h2>
          <button onClick={() => nav('/app/connections')} className="text-sm text-violet-600 hover:text-violet-700 font-medium">Manage connections</button>
        </div>
        <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
          {connections.slice(0, 8).map((conn) => (
            <div key={conn.id} className={`p-4 rounded-xl border ${conn.status === 'connected' ? 'bg-white border-stone-200' : 'bg-stone-50 border-dashed border-stone-300'}`}>
              <div className="flex items-center gap-3">
                <PlatformIcon platform={conn.id} size="md" />
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-stone-900">{conn.name}</p>
                  {conn.status === 'connected'
                    ? <p className="text-xs text-stone-500 truncate">{conn.account}</p>
                    : <button onClick={() => nav('/app/connections')} className="text-xs text-violet-600 font-medium">Connect →</button>}
                </div>
                {conn.status === 'connected' && <div className="w-2 h-2 rounded-full bg-emerald-500" />}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Recent activity */}
      <div>
        <div className="flex items-center justify-between mb-3"><h2 className="text-sm font-semibold text-stone-500 uppercase tracking-wide">Recent activity</h2><button onClick={() => nav('/app/drafts')} className="text-sm text-violet-600 font-medium">View all</button></div>
        <div className="bg-white border border-stone-200 rounded-xl overflow-x-auto">
          <table className="w-full min-w-[640px]">
            <thead><tr className="bg-stone-50 border-b border-stone-200">{['Post', 'Platforms', 'Status', 'Scheduled', ''].map((h) => <th key={h} className="text-left px-5 py-3 text-xs font-semibold text-stone-500 uppercase tracking-wide">{h}</th>)}</tr></thead>
            <tbody>
              {[{ title: '5 AI trends reshaping marketing', platforms: ['linkedin', 'twitter'], status: 'scheduled', time: 'Apr 17, 9:00 AM' }, { title: 'Product launch announcement', platforms: ['linkedin', 'facebook', 'instagram'], status: 'draft', time: '—' }, { title: 'Customer success story', platforms: ['linkedin', 'twitter'], status: 'published', time: 'Apr 15, 2:00 PM' }].map((post) => (
                <tr key={post.title} className="border-b border-stone-100 hover:bg-stone-50 transition-colors">
                  <td className="px-5 py-4 font-medium text-stone-900">{post.title}</td>
                  <td className="px-5 py-4"><div className="flex gap-1.5">{post.platforms.map((p) => <PlatformIcon key={p} platform={p} />)}</div></td>
                  <td className="px-5 py-4"><Badge status={post.status} /></td>
                  <td className="px-5 py-4 text-sm text-stone-500">{post.time}</td>
                  <td className="px-5 py-4"><button onClick={() => toast('Opening composer…')} className="text-sm text-violet-600 hover:text-violet-700 font-medium">Edit</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
